import { resolveAvatarRules } from '../../../utils/game/avatarRules';
import { playSound } from '../../../utils/game/sounds';
import { msg } from '../../../utils/game/logMessage';

// Mulligan VIEW orchestration, extracted from GameBoard.jsx. The opening draw
// and the whole mulligan procedure are SERVER-owned (start director +
// mulligan intent). This module only surfaces the keep/return UI and sends
// the one resolve intent. The mulligan is two-stage per player: 'prompt'
// (yes/no ask) then 'selecting' (card-picking) then 'done'. In a solo match
// (isCasualSandbox) the one pilot mulligans BOTH seats. P2's state lives on
// suffixed keys (mulliganPhase2 / mulliganFlaggedIds2, handCards2); in
// multiplayer the local seat always uses the unsuffixed keys.
//
// Host contract: props.isCasualSandbox, state (isHost, activePlayerNum,
// handCards/handCards2, mulligan phase/flag keys, currentTurn,
// gameState.avatarCard), setState, meshes, _sendMatchIntent, logAction,
// autoSave, _closePrompt, _stepDrawCard, _pendingDrawPromptFor,
// mulliganOverlayRef, _unmounted.

// Opponent-safe mulligan summary. Counts only, never card identities.
// Returns `null` for an empty mulligan so callers can skip logging it.
export function buildMulliganSummaryText({ spellbook = 0, atlas = 0, playerNum = null } = {}) {
  const total = spellbook + atlas;
  if (total <= 0) return null;
  const detail = spellbook && atlas
    ? msg('{n} Spellbook, {m} Atlas', { n: spellbook, m: atlas })
    : spellbook ? msg('Spellbook') : msg('Atlas');
  const values = { p: playerNum, n: total, detail };
  if (playerNum != null) {
    return total === 1
      ? msg('P{p} mulliganed 1 card ({detail})', values)
      : msg('P{p} mulliganed {n} cards ({detail})', values);
  }
  return total === 1
    ? msg('Mulliganed 1 card ({detail})', values)
    : msg('Mulliganed {n} cards ({detail})', values);
}

// State keys for a player's mulligan phase + flagged-card set. In
// multiplayer the local seat always uses the unsuffixed keys. Seat
// identity is fixed by isHost, not by the activePlayer pointer (which
// tracks whose TURN it is, not the local seat), so this never hides
// the banner on Guest's client at match start.
export function mulliganKeys(host, playerNum) {
  if (!host.props.isCasualSandbox) {
    return { phase: 'mulliganPhase', flags: 'mulliganFlaggedIds' };
  }
  return playerNum === 2
    ? { phase: 'mulliganPhase2', flags: 'mulliganFlaggedIds2' }
    : { phase: 'mulliganPhase',  flags: 'mulliganFlaggedIds' };
}

// Local player's seat number (1 or 2). In casual sandbox this follows
// the Tab-swapped active side; in multiplayer it's fixed by isHost.
// Used for mulligan plumbing so avatar / hand / pile lookups key off
// the local seat instead of whose turn it currently is.
export function localPlayerNum(host) {
  if (host.props.isCasualSandbox) return host.state.activePlayerNum;
  return host.state.isHost ? 1 : 2;
}

// Mulligan phase / flags state belonging to the card's own player.
// In multiplayer this is always the local player's mulligan state;
// in casual sandbox it follows the card's rotated flag so a click
// on a P2 hand card checks mulliganPhase2, not P1's.
export function mulliganPhaseForCard(host, cardInstance) {
  if (!host.props.isCasualSandbox) return host.state.mulliganPhase;
  return host.state[cardInstance.rotated ? 'mulliganPhase2' : 'mulliganPhase'];
}

// Locate the avatar card instance for a given player, used when the
// current active player isn't the one whose rules we need to read
// (e.g. opening draw for P2 while the local user is controlling P1).
export function findAvatarForPlayer(host, playerNum) {
  const rotated = playerNum === 2;
  // The avatarCard on gameState is authoritative for the local deck
  // spawn; for the other player we look through on-table meshes to
  // find their Avatar-typed card.
  if (!rotated && host.state.gameState?.avatarCard) {
    // In a standard single-deck session the avatarCard slot holds P1.
    if (!host.state.gameState.avatarCard.rotated) return host.state.gameState.avatarCard;
  }
  for (const mesh of host.meshes.values()) {
    const c = mesh.userData?.cardInstance;
    if (c && c.type === 'Avatar' && !!c.rotated === rotated) return c;
  }
  return null;
}

// Open the two-stage mulligan prompt for a seat AFTER the server has dealt the
// opening hand (auth). No drawing here. The server (start director) owns the
// opening DRAW and the client renders the dealt hand; this only surfaces the
// keep/mulligan decision. Idempotent: a re-fold while already prompting/decided
// is a no-op. matchPhase stays server-owned ('rollOff' until both mulligans
// done); we never flip it locally.
export function openMulliganPrompt(host, n) {
  const keys = mulliganKeys(host, n);
  if (host.state[keys.phase]) return;
  host.setState({ [keys.phase]: 'prompt', [keys.flags]: new Set() });
}

// Move a player from the initial yes/no prompt into card-picking.
export function acceptMulligan(host) {
  const playerNum = localPlayerNum(host);
  const keys = mulliganKeys(host, playerNum);
  if (host.state[keys.phase] !== 'prompt') return;
  playSound('uiClick');
  host.setState({ [keys.phase]: 'selecting' });
}

// Toggle a hand card's mulligan flag. Enforces the avatar's total
// cap by shaking the banner + rejecting a 4th addition. Unflagging
// always works (you can never be above the cap by unflagging).
//
// In casual sandbox, the flag targets whichever mulligan phase is
// currently active (P1 or P2). The card's .rotated flag decides
// which side it belongs to, and we refuse to flag cards for a
// player whose mulligan phase isn't active.
export function toggleMulliganFlag(host, cardInstance) {
  const playerNum = cardInstance.rotated ? 2 : 1;
  const keys = mulliganKeys(host, playerNum);
  // Only the 'selecting' stage accepts flag toggles. 'prompt'
  // waits for the user's yes/no decision first.
  if (host.state[keys.phase] !== 'selecting') return;
  const avatarCard = findAvatarForPlayer(host, playerNum);
  const { mulligan } = resolveAvatarRules(avatarCard);
  const current = host.state[keys.flags] || new Set();
  const next = new Set(current);
  if (next.has(cardInstance.id)) {
    next.delete(cardInstance.id);
  } else {
    if (next.size >= mulligan.maxCards) {
      if (host.mulliganOverlayRef) host.mulliganOverlayRef.shake();
      playSound('cardFlip');
      return;
    }
    next.add(cardInstance.id);
  }
  playSound('cardFlip');
  host.setState({ [keys.flags]: next });
}

// Confirm the mulligan for the currently-active player. SERVER-owned: one
// intent carries the picked ids and the server performs the whole procedure
// (validate against the authoritative hand + avatar cap, return each pick to
// its deck's bottom, redraw the same counts, mark the seat done. 'playing'
// flips in the reducer once both seats are). The client's only writes are
// VIEW prediction: drop the returned cards from the local hand fan now (the
// pile reconcile fold confirms them into the deck render on the diff) and let
// the redraws land through the animated server-draw fold
// (applyHandAdditionsFromMirror. Stagger + sound included).
export function executeMulligan(host) {
  const playerNum = localPlayerNum(host);
  const keys = mulliganKeys(host, playerNum);
  // Only the 'selecting' stage actually executes. 'prompt' has
  // to go through acceptMulligan() first.
  if (host.state[keys.phase] !== 'selecting') return;
  const flagged = host.state[keys.flags] || new Set();
  if (flagged.size === 0) return keepHand(host);

  const seat = playerNum === 2 ? 'p2' : 'p1';
  // One resolve intent per seat until the server confirms (fold clears it).
  // a rapid double-click must not double-send/double-log.
  if (host._mulliganIntentSent?.[seat]) return;
  (host._mulliganIntentSent || (host._mulliganIntentSent = {}))[seat] = true;

  // Local hand state key: in multiplayer the local seat is always the
  // unsuffixed `handCards`; in casual sandbox the P2 side lives on `handCards2`.
  const handKey = (host.props.isCasualSandbox && playerNum === 2) ? 'handCards2' : 'handCards';
  const cardsToReturn = (host.state[handKey] || []).filter((c) => flagged.has(c.id));
  const spellbookReturned = cardsToReturn.filter((c) => !c.isSite).length;
  const atlasReturned     = cardsToReturn.filter((c) =>  c.isSite).length;

  host._sendMatchIntent({
    domain: 'mulligan',
    kind: 'resolve',
    seat,
    returnIds: cardsToReturn.map((c) => c.id),
  });
  // View prediction: the returned cards leave the hand fan immediately.
  host.setState((s) => ({ [handKey]: (s[handKey] || []).filter((c) => !flagged.has(c.id)) }));

  // Counts only, never identities. Sorcery mulligans are private.
  // In sandbox both seats are local so the line is prefixed with the
  // player number for clarity; in multiplayer the actor's name prefix
  // is rendered by the logbook component from the entry's actor field.
  const summary = buildMulliganSummaryText({
    spellbook: spellbookReturned,
    atlas: atlasReturned,
    playerNum: host.props.isCasualSandbox ? playerNum : null,
  });
  if (summary) host.logAction(summary);

  // The phase flips to 'done' when the SERVER confirms (the mulliganDone
  // fold). Never optimistically. A lost intent leaves the banner up, and the
  // player's retry re-sends; the server's once-per-seat gate makes any
  // duplicate a no-op. Clear the flags so a retry starts from a clean pick.
  host.setState({ [keys.flags]: new Set() });
}

// Skip the mulligan for the active player. The keep is the same SERVER
// intent with no returns. The server marks the seat mulligan-done (and the
// reducer flips to 'playing' once both seats are).
export function keepHand(host) {
  const playerNum = localPlayerNum(host);
  const keys = mulliganKeys(host, playerNum);
  // Works in both 'prompt' and 'selecting' stages: the user
  // either declines from the yes/no prompt or cancels from the
  // card-picking stage. Either way the result is the same. No
  // cards are returned.
  const phase = host.state[keys.phase];
  if (phase !== 'prompt' && phase !== 'selecting') return;
  const seat = playerNum === 2 ? 'p2' : 'p1';
  if (host._mulliganIntentSent?.[seat]) return;
  (host._mulliganIntentSent || (host._mulliganIntentSent = {}))[seat] = true;
  host._sendMatchIntent({
    domain: 'mulligan',
    kind: 'resolve',
    seat,
    returnIds: null,
  });
  if (host.props.isCasualSandbox) {
    host.logAction('P{p} kept opening hand', { p: playerNum });
  } else {
    host.logAction('Kept opening hand');
  }
  // Completion (phase 'done' + the view hooks) folds from the server's
  // mulliganDone confirm. See applyMulliganDoneFromMirror.
  host.setState({ [keys.flags]: new Set() });
}

// Fold SERVER mulligan completion onto the local banner state: when the
// mirror marks one of our seats done and the local phase isn't, flip it to
// 'done' and run the view hooks (solo perspective handoff, deferred draw
// prompt replay, the resurrection-proof save). Confirmation-driven rather
// than optimistic, so a lost mulligan intent leaves the banner up for a
// retry and the both-seats gate can never wedge on a phantom local 'done'.
export function applyMulliganDoneFromMirror(host, mirrorState) {
  if (mirrorState?.phase?.startStep !== 'openingDraw'
    && mirrorState?.phase?.startStep !== 'done') return;
  const done = mirrorState.phase?.mulliganDone;
  if (!done) return;
  const seats = host.props.isCasualSandbox
    ? [['p1', 1], ['p2', 2]]
    : [[host.state.isHost ? 'p1' : 'p2', host.state.isHost ? 1 : 2]];
  for (const [seat, n] of seats) {
    if (!done[seat]) continue;
    if (host._mulliganIntentSent) host._mulliganIntentSent[seat] = false;
    const keys = mulliganKeys(host, n);
    if (host.state[keys.phase] === 'done') continue;
    host.setState(
      { [keys.phase]: 'done', [keys.flags]: new Set() },
      () => afterMulliganComplete(host, seat),
    );
  }
}

// Mulligan completion hook. Pure VIEW bookkeeping. The server owns the
// authoritative half (mulligan-done marker, the 'playing' flip, the resource
// seed, and the going-first player's turn-start trigger net all commit
// server-side off the mulligan intent).
export function afterMulliganComplete(host, player) {
  // Solo (one pilot, both seats): hand the perspective to the OTHER seat's
  // still-open mulligan so the pilot resolves both without tabbing (Tab is
  // the auth PASS_TURN now, which the server rejects pre-'playing'). Once
  // both are done, snap the perspective back to the going-first seat.
  if (host.props.isCasualSandbox) {
    const otherNum = player === 'p2' ? 1 : 2;
    const otherStage = host.state[mulliganKeys(host, otherNum).phase];
    if (otherStage === 'prompt' || otherStage === 'selecting') {
      host.setState({ activePlayerNum: otherNum });
    } else {
      host.setState({ activePlayerNum: host.state.currentTurn === 'p2' ? 2 : 1 });
    }
  }
  replayDeferredDrawPrompt(host, player);
  // Persist + emit this seat's state NOW that mulligan is resolved. Mulligan
  // completion (keepHand / executeMulligan) otherwise triggers NO save, so
  // the server's per-seat private kept the opening-draw 'prompt'. And a
  // save-and-leave-then-relaunch pulled it back, resurrecting the mulligan
  // UI even though the public board (host-emitted) was current. Forced so it
  // doesn't wait on the debounce and can't be lost to a fast Exit→disconnect.
  host.autoSave({ force: true }).catch(() => {});
}

// If _stepDrawCard parked a start-phase draw prompt for `player`
// while their mulligan was still open, fire it now that they've
// resolved. No-op when nothing is deferred or it's for the other
// player. They get to resolve their own mulligan independently.
export function replayDeferredDrawPrompt(host, player) {
  if (host._pendingDrawPromptFor !== player) return;
  host._pendingDrawPromptFor = null;
  host._closePrompt(`deferred-draw-${player}`);
  host._stepDrawCard(player);
}
